import { useState, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { applySeo } from "@/lib/seo";
import { trpc } from "@/lib/trpc";
import { generateLokalPdf } from "@/lib/generatePdf";
import { AgencyLogo } from "@/components/AgencyLogo";
import { BarometerNav } from "@/components/BarometerNav";
import { CrossSelling } from "@/components/CrossSelling";
import { BAROMETER_PRICES, BAROMETER_MIT_VIDEOCALL, formatPrice } from "@shared/prices";
import { CheckoutButton } from "@/components/CheckoutButton";


const LOGO_URL = "/manus-storage/logo_2d439bd1.jpg";
const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";

const C = {
  teal: "#115751",
  tealDark: "#0d4440",
  tealLight: "#11575115",
  bordeaux: "#6d0000",
  bg: "#ffffff",
  bgGray: "#f2f2f2",
  text: "#000000",
  muted: "#6b7280",
};

const BRANCHEN = [
  { value: "", label: "Branche wählen (optional)" },
  { value: "dienstleister", label: "Dienstleister / Beratung" },
  { value: "ecommerce", label: "E-Commerce / Online-Shop" },
  { value: "gastronomie", label: "Gastronomie / Hotel / Tourismus" },
  { value: "gesundheit", label: "Gesundheit / Wellness / Beauty" },
  { value: "b2b", label: "B2B / Software / Technologie" },
  { value: "handwerk", label: "Handwerk / Bau / Immobilien" },
  { value: "kreativ", label: "Kreativbranche / Agentur / Design" },
  { value: "sonstige", label: "Sonstige" },
];

// ─── Ergebnisseite (inline) ───────────────────────────────────────────────────
function GeoResultsView({ data, onReset }: { data: any; onReset: () => void }) {
  const d = data;
  const [openTips, setOpenTips] = useState<number[]>([0]);
  const [openDims, setOpenDims] = useState<string[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  const DIMENSION_LABELS_MAP: Record<string, { label: string; icon: string; beschreibung: string }> = {
    lokale_identitaet: { label: "Lokale Identität", icon: "/manus-storage/icon_lokale_identitaet_02a64c71.png", beschreibung: "Adresse, Stadtname, Region, lokale Sprache." },
    lokale_seo: { label: "Lokale SEO-Signale", icon: "/manus-storage/23_1ab7147e.svg", beschreibung: "Schema.org-Markup, Meta-Tags, NAP-Konsistenz." },
    google_business: { label: "Google Business", icon: "/manus-storage/icon_google_business_a5d1d0a7.png", beschreibung: "Google Maps, GMB-Verlinkung, Bewertungen." },
    vertrauen: { label: "Vertrauen & Glaubwürdigkeit", icon: "/manus-storage/icon_vertrauen_5befd394.png", beschreibung: "Impressum, Datenschutz, Zertifikate, Bewertungen." },
    lokale_inhalte: { label: "Lokale Inhalte", icon: "/manus-storage/icon_lokale_inhalte_2668a69f.png", beschreibung: "Lokale Referenzen, regionale Projekte, Stadtbezüge." },
    technisch: { label: "Mobile & Technisch", icon: "/manus-storage/33_d51b7819.svg", beschreibung: "Mobile-Optimierung, Ladezeit, HTTPS." },
    soziale_signale: { label: "Soziale Signale", icon: "/manus-storage/icon_soziale_signale_c47c0468.png", beschreibung: "Social-Media-Präsenz, lokale Community." },
    kontakt_conversion: { label: "Kontakt & Conversion", icon: "/manus-storage/icon_kontakt_conversion_eeab8a33.png", beschreibung: "Klickbare Telefonnummer, Kontaktformular, CTAs." },
  };
  const PRIO_COLORS: Record<string, string> = { kritisch: "#ef4444", hoch: "#f97316", mittel: "#eab308", niedrig: "#22c55e" };
  const PRIO_LABELS: Record<string, string> = { kritisch: "🔴 Kritisch", hoch: "🟠 Hoch", mittel: "🟡 Mittel", niedrig: "🟢 Niedrig" };

  const handlePdfDownload = () => {
    setPdfLoading(true);
    try {
      generateLokalPdf(d as Record<string, unknown>);
    } catch (err) {
      console.error("[PDF-Fehler Lokal]", err);
      alert("PDF konnte nicht erstellt werden: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setTimeout(() => setPdfLoading(false), 1500);
    }
  };

  const toggleTip = (i: number) => setOpenTips(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  const toggleDim = (k: string) => setOpenDims(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);

  const maxScore = d.max_score || 135;
  const pct = Math.round((d.total_score / maxScore) * 100);
  const scoreColor = pct >= 70 ? "#22c55e" : pct >= 40 ? "#eab308" : "#ef4444";
  const kritischeTipps = (d.optimierungen || []).filter((t: any) => t.prioritaet === "kritisch");
  const hoheTipps = (d.optimierungen || []).filter((t: any) => t.prioritaet === "hoch");
  const mittlereTipps = (d.optimierungen || []).filter((t: any) => t.prioritaet === "mittel");
  const niedrigeTipps = (d.optimierungen || []).filter((t: any) => t.prioritaet === "niedrig");

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Header */}
      <header className="border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-50">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="WERBEAGENTUR Larissa Girenko" className="w-10 h-10 object-contain" />
          <span className="text-black font-black text-sm tracking-widest uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Lokal-Barometer™</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handlePdfDownload} disabled={pdfLoading}
            className="flex items-center gap-2 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 transition-colors disabled:opacity-50"
            style={{ backgroundColor: C.teal, fontFamily: "'DM Sans', sans-serif" }}>
            {pdfLoading ? "⏳ Wird erstellt …" : "📄 PDF"}
          </button>
          <nav className="flex items-center gap-4">
            <a href="/" className="text-xs tracking-widest uppercase text-gray-400 hover:text-black transition-colors">Sensorik</a>
            <a href="/geo-barometer" className="text-xs tracking-widest uppercase text-gray-400 hover:text-black transition-colors">GEO</a>
          </nav>
          <button onClick={onReset}
            className="text-xs text-gray-500 hover:text-black border border-gray-200 hover:border-gray-400 px-4 py-2 transition-colors tracking-widest uppercase"
            style={{ fontFamily: "'DM Sans', sans-serif" }}>← Neue Analyse</button>
        </div>
      </header>
      {/* URL Banner */}
      <div className="bg-[#f2f2f2] border-b border-gray-200 px-6 py-3 flex items-center gap-3 flex-wrap">
        <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
        <span className="text-gray-500 text-xs font-medium uppercase tracking-widest" style={{ fontFamily: "'DM Sans', sans-serif" }}>Analysiert:</span>
        <span className="text-black text-sm font-medium truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.url}</span>
        {d.page_title && <span className="text-gray-400 text-xs hidden md:block" style={{ fontFamily: "'DM Sans', sans-serif" }}>· {d.page_title}</span>}
        {d.pages_crawled && d.pages_crawled.length > 0 && (
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <span className="text-gray-400 text-xs uppercase tracking-widest" style={{ fontFamily: "'DM Sans', sans-serif" }}>Seiten:</span>
            {d.pages_crawled.map((pg: any, i: number) => (
              <span key={i} className="text-[10px] px-2 py-0.5 border font-medium uppercase tracking-wider"
                style={{ backgroundColor: pg.error ? '#fef2f2' : '#f0fdf4', borderColor: pg.error ? '#fecaca' : '#bbf7d0', color: pg.error ? '#dc2626' : '#16a34a', fontFamily: "'DM Sans', sans-serif" }}>
                {pg.type}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-10">
        {/* Score-Übersicht */}
        <div className="border border-gray-200 p-8 bg-white">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="flex-shrink-0 text-center">
              <div className="relative w-36 h-36 mx-auto">
                <svg viewBox="0 0 120 120" className="w-36 h-36 -rotate-90">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#f2f2f2" strokeWidth="10" />
                  <circle cx="60" cy="60" r="50" fill="none" stroke={scoreColor} strokeWidth="10" strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 50}`}
                    strokeDashoffset={`${2 * Math.PI * 50 * (1 - pct / 100)}`} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-black text-black" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{d.total_score}</span>
                  <span className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>/ {maxScore}</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2 uppercase tracking-widest" style={{ fontFamily: "'DM Sans', sans-serif" }}>{pct}% Lokal-Score</p>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">{d.reifegrad?.emoji || "📊"}</span>
                <div>
                  <h2 className="text-2xl font-black uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", color: d.reifegrad?.color || C.teal }}>{d.reifegrad?.label || ""}</h2>
                  <p className="text-xs text-gray-400 uppercase tracking-widest" style={{ fontFamily: "'DM Sans', sans-serif" }}>Lokal-Reifegrad</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.reifegrad?.desc || ""}</p>
              <div className="space-y-2">
                {Object.entries(d.scores || {}).map(([key, dim]: [string, any]) => {
                  const meta = DIMENSION_LABELS_MAP[key] || { label: key, icon: "●", beschreibung: "" };
                  const dimPct = dim.max > 0 ? (dim.score / dim.max) * 100 : 0;
                  const dimColor = dimPct >= 70 ? "#22c55e" : dimPct >= 40 ? "#eab308" : "#ef4444";
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <img src={meta.icon} alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                      <span className="text-xs text-gray-500 w-40 flex-shrink-0 truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{meta.label}</span>
                      <div className="flex-1 h-1.5 bg-gray-200 overflow-hidden">
                        <div className="h-full" style={{ width: `${dimPct}%`, backgroundColor: dimColor }} />
                      </div>
                      <span className="text-xs font-bold w-12 text-right" style={{ color: dimColor, fontFamily: "'DM Sans', sans-serif" }}>{dim.score}/{dim.max}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
            {[
              { label: "Adresse sichtbar", value: !!(d.scores?.lokale_identitaet?.details?.adresse as any)?.has_plz, icon: "/manus-storage/icon_lokale_identitaet_02a64c71.png" },
              { label: "Telefon klickbar", value: !!(d.scores?.lokale_identitaet?.details?.telefon as any)?.clickable, icon: "/manus-storage/icon_kontakt_conversion_eeab8a33.png" },
              { label: "Google Maps", value: !!(d.scores?.lokale_identitaet?.details?.maps as any)?.found, icon: "/manus-storage/icon_google_business_a5d1d0a7.png" },
              { label: "Impressum", value: !!(d.scores?.vertrauen?.details?.impressum as any)?.found, icon: "/manus-storage/icon_vertrauen_5befd394.png" },
            ].map((s) => (
              <div key={s.label} className="text-center p-3 border" style={{ backgroundColor: s.value ? '#f0fdf4' : '#fef2f2', borderColor: s.value ? '#bbf7d0' : '#fecaca' }}>
                <img src={s.icon} alt="" className="w-7 h-7 mb-1 object-contain" style={{ opacity: 0.85 }} />
                <p className="text-lg font-black" style={{ color: s.value ? '#16a34a' : '#dc2626' }}>{s.value ? '✓' : '✗'}</p>
                <p className="text-xs mt-1 uppercase tracking-wider leading-tight" style={{ color: s.value ? '#15803d' : '#b91c1c', fontFamily: "'DM Sans', sans-serif" }}>{s.label}</p>
              </div>
            ))}
          </div>
          {/* Cookie Consent */}
          {d.cookie_consent && (
            <div className={`mt-4 p-4 border ${d.cookie_consent.has_consent ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
              <div className="flex items-start gap-3">
                <span className="text-xl">{d.cookie_consent.has_consent ? "🍪" : "⚠️"}</span>
                <div>
                  <p className="text-xs font-black uppercase tracking-widest mb-1" style={{ color: d.cookie_consent.has_consent ? "#16a34a" : "#dc2626", fontFamily: "'DM Sans', sans-serif" }}>
                    {d.cookie_consent.has_consent ? "DSGVO Cookie-Consent erkannt" : "Kein Cookie-Consent gefunden"}
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {d.cookie_consent.has_consent
                      ? `Erkannte Tools: ${d.cookie_consent.tools.join(", ")}${d.cookie_consent.has_blocked_placeholders ? " · Externe Inhalte werden erst nach Zustimmung geladen." : ""}`
                      : "Kein Cookie-Consent-Tool erkannt. Empfehlung: Borlabs Cookie, Complianz oder Real Cookie Banner einsetzen."}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Dimensionen im Detail */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-xl uppercase tracking-tight text-black" style={{ fontFamily: "'Roboto', sans-serif" }}>Dimensionen im Detail</h2>
            <button onClick={() => setOpenDims(Object.keys(d.scores || {}))}
              className="text-xs uppercase tracking-widest transition-colors" style={{ color: C.bordeaux, fontFamily: "'DM Sans', sans-serif" }}>Alle aufklappen ↓</button>
          </div>
          <div className="space-y-2">
            {Object.entries(d.scores || {}).map(([key, dim]: [string, any]) => {
              const meta = DIMENSION_LABELS_MAP[key] || { label: key, icon: "●", beschreibung: "" };
              const dimPct = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
              const isOpen = openDims.includes(key);
              return (
                <div key={key} className="bg-white border border-gray-200 overflow-hidden">
                  <button className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors" onClick={() => toggleDim(key)}>
                    <img src={meta.icon} alt="" className="w-6 h-6 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-sm text-black" style={{ fontFamily: "'DM Sans', sans-serif" }}>{meta.label}</span>
                        <span className="text-xs font-black ml-4 flex-shrink-0" style={{ color: dimPct >= 70 ? "#22c55e" : dimPct >= 40 ? "#eab308" : "#ef4444", fontFamily: "'DM Sans', sans-serif" }}>
                          {dim.score}/{dim.max} ({dimPct}%)
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-1.5 bg-gray-200 overflow-hidden">
                          <div className="h-full" style={{ width: `${dimPct}%`, backgroundColor: dimPct >= 70 ? "#22c55e" : dimPct >= 40 ? "#eab308" : "#ef4444" }} />
                        </div>
                      </div>
                    </div>
                    <span className="text-gray-400 text-xs flex-shrink-0">{isOpen ? "▲" : "▼"}</span>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 border-t border-gray-100 bg-[#f2f2f2]">
                      <p className="text-xs text-gray-500 leading-relaxed mt-4 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>{meta.beschreibung}</p>
                      <div className="space-y-2">
                        {Object.entries(dim.details || {}).map(([detailKey, detailVal]: [string, any]) => {
                          const isPositive = detailVal?.found === true || (typeof detailVal?.score === "number" && detailVal.score > 0);
                          const isNegative = detailVal?.found === false || (typeof detailVal?.score === "number" && detailVal.score === 0);
                          return (
                            <div key={detailKey} className="flex items-center gap-2 text-xs">
                              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-xs font-bold"
                                style={{ color: isPositive ? "#16a34a" : isNegative ? "#dc2626" : "#6b7280" }}>
                                {isPositive ? "✓" : isNegative ? "✗" : "○"}
                              </span>
                              <span className="text-gray-500 capitalize" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                                {detailKey.replace(/_/g, " ")}
                                {typeof detailVal?.score === "number" ? ` (${detailVal.score}/${detailVal.max})` : ""}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {/* Optimierungsempfehlungen */}
        {(d.optimierungen || []).length > 0 && (
          <div>
            <h2 className="font-bold text-xl uppercase tracking-tight text-black mb-2" style={{ fontFamily: "'Roboto', sans-serif" }}>Optimierungsempfehlungen</h2>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>Nach Priorität sortiert – mit konkreten Schritt-für-Schritt-Anleitungen.</p>
            <div className="grid grid-cols-4 gap-3 mb-6">
              {[
                { label: "Kritisch", count: kritischeTipps.length, color: "#dc2626" },
                { label: "Hoch", count: hoheTipps.length, color: "#ea580c" },
                { label: "Mittel", count: mittlereTipps.length, color: "#d97706" },
                { label: "Niedrig", count: niedrigeTipps.length, color: "#16a34a" },
              ].map(s => (
                <div key={s.label} className="bg-white border border-gray-200 p-3 text-center">
                  <p className="text-2xl font-black" style={{ color: s.color, fontFamily: "'Bebas Neue', sans-serif" }}>{s.count}</p>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mt-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>{s.label}</p>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              {(d.optimierungen || []).map((tip: any, i: number) => (
                <div key={i} className="bg-white border border-gray-200 overflow-hidden"
                  style={{ borderLeftColor: PRIO_COLORS[tip.prioritaet] || C.bordeaux, borderLeftWidth: 4 }}>
                  <button className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors" onClick={() => toggleTip(i)}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-xs font-black uppercase tracking-wider px-2 py-1 flex-shrink-0"
                        style={{ color: PRIO_COLORS[tip.prioritaet], border: `1px solid ${PRIO_COLORS[tip.prioritaet]}` }}>
                        {PRIO_LABELS[tip.prioritaet] || tip.prioritaet}
                      </span>
                      <span className="text-sm font-bold truncate text-black" style={{ fontFamily: "'DM Sans', sans-serif" }}>{tip.titel}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                      {(tip.zeitaufwand || tip.aufwand) && <span className="text-xs text-gray-400 hidden md:block" style={{ fontFamily: "'DM Sans', sans-serif" }}>⏱ {tip.zeitaufwand || tip.aufwand}</span>}
                      <span className="text-gray-400 text-xs">{openTips.includes(i) ? "▲" : "▼"}</span>
                    </div>
                  </button>
                  {openTips.includes(i) && (
                    <div className="px-5 pb-6 border-t border-gray-100 bg-[#f2f2f2]">
                      <p className="text-xs uppercase tracking-widest font-bold mb-3 mt-4" style={{ color: C.bordeaux, fontFamily: "'DM Sans', sans-serif" }}>{tip.dimension}</p>
                      {tip.warum && (
                        <div className="bg-white border border-gray-200 p-4 mb-4">
                          <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>💡 Warum das wichtig ist</p>
                          <p className="text-gray-600 text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{tip.warum}</p>
                        </div>
                      )}
                      <p className="text-gray-600 text-sm leading-relaxed mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>{tip.beschreibung}</p>
                      {tip.schritte && tip.schritte.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: C.bordeaux, fontFamily: "'DM Sans', sans-serif" }}>📋 Schritt-für-Schritt</p>
                          <div className="space-y-2">
                            {tip.schritte.map((schritt: string, si: number) => (
                              <div key={si} className="flex gap-3">
                                <span className="w-6 h-6 text-white text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: C.bordeaux }}>{si + 1}</span>
                                <p className="text-gray-600 text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{schritt}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {tip.tools && tip.tools.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>🛠 Empfohlene Tools</p>
                          <div className="flex flex-wrap gap-2">
                            {tip.tools.map((tool: string, ti: number) => (
                              <span key={ti} className="text-xs bg-white border border-gray-200 px-3 py-1 text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>{tool}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-4 pt-3 border-t border-gray-200">
                        {(tip.zeitaufwand || tip.aufwand) && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 uppercase tracking-wider" style={{ fontFamily: "'DM Sans', sans-serif" }}>⏱ Zeitaufwand:</span>
                            <span className="text-xs font-bold text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>{tip.zeitaufwand || tip.aufwand}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400 uppercase tracking-wider" style={{ fontFamily: "'DM Sans', sans-serif" }}>📈 Wirkung:</span>
                          <span className="text-xs font-bold" style={{ color: C.bordeaux, fontFamily: "'DM Sans', sans-serif" }}>{tip.wirkung}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* CTA */}
        <div className="p-8" style={{ backgroundColor: C.bordeaux }}>
          <p className="text-xs tracking-[0.3em] uppercase text-white/60 mb-3 text-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>Nächster Schritt</p>
          <h3 className="text-2xl font-bold uppercase mb-3 text-white text-center" style={{ fontFamily: "'Roboto', sans-serif" }}>Dein persönlicher Lokal-Aktionsplan</h3>
          <p className="text-white/80 text-sm mb-6 max-w-lg mx-auto leading-relaxed text-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Der Lokal-Barometer zeigt, wo du stehst. Wähle jetzt deine Buchungsoption.
          </p>
          {/* Zwei-Optionen-Karten */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto mb-6">
            <div className="bg-white p-5 flex flex-col">
              <p className="text-xs font-bold tracking-widest uppercase mb-1 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Option 1</p>
              <h4 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.bordeaux }}>Vollständige Analyse</h4>
              <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Alle Befunde, priorisierte Empfehlungen und persönlicher PDF-Report. Lieferung in 72 Stunden.</p>
              <div className="text-xl font-black mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.bordeaux }}>{formatPrice(BAROMETER_PRICES.lokal)}</div>
              <CheckoutButton productId="lokal_analyse" label="Analyse buchen" accentColor={C.bordeaux} returnPath="/lokal" fullWidth />
            </div>
            <div className="border-2 bg-white p-5 flex flex-col relative" style={{ borderColor: "#6d0000" }}>
              <div className="absolute -top-3 left-4 px-3 py-1 text-white text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Empfohlen</div>
              <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Option 2</p>
              <h4 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#6d0000" }}>Analyse + VideoCall</h4>
              <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Vollständige Analyse + 30-minütige persönliche Ergebnis-Interpretation mit Larissa per VideoCall.</p>
              <div className="text-xl font-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#6d0000" }}>{formatPrice(BAROMETER_MIT_VIDEOCALL)}</div>
              <p className="text-xs text-gray-400 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>inkl. 30-Min-VideoCall (+79 €)</p>
              <CheckoutButton productId="lokal_mit_videocall" label="Analyse + VideoCall buchen" accentColor="#6d0000" returnPath="/lokal" fullWidth />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={handlePdfDownload} disabled={pdfLoading}
              className="text-white font-bold text-sm uppercase tracking-widest px-8 py-3 transition-colors disabled:opacity-50"
              style={{ backgroundColor: "#115751", fontFamily: "'DM Sans', sans-serif" }}>
              {pdfLoading ? "⏳ Wird erstellt …" : "📄 PDF-Report herunterladen"}
            </button>
            <button onClick={onReset}
              className="border border-white/40 text-white font-bold text-sm uppercase tracking-widest px-8 py-3 hover:bg-white/10 transition-colors"
              style={{ fontFamily: "'DM Sans', sans-serif" }}>Neue Analyse starten</button>
          </div>
        </div>
      </div>
      <footer className="border-t border-gray-200 px-6 py-8 text-center bg-white">
        <p className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>© 2026 WERBEAGENTUR Larissa Girenko · Lokal-Barometer™ · Dein Schlüssel zum Marketingerfolg</p>
        <p className="text-xs mt-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          <a href="/impressum" className="hover:underline" style={{ color: '#6d0000' }}>Impressum</a>
          <span className="text-gray-300 mx-2">·</span>
          <a href="/datenschutz" className="hover:underline" style={{ color: '#6d0000' }}>Datenschutzerklärung</a>
        </p>
      </footer>
    </div>
  );
}

export default function GeoHome() {
  const { user } = useAuth();
  const isAdminUser = user?.role === "admin";
  useEffect(() => applySeo({
    title: "Lokal-Barometer™ – Wirst du lokal bei Google gefunden? | WERBEAGENTUR Larissa Girenko",
    description: "Google Maps, Schema.org, NAP-Konsistenz, lokale Inhalte: Lokal-Barometer™ prüft 135+ Kriterien lokaler Sichtbarkeit. Vollanalyse ab 97 €.",
    path: "/lokal-barometer",
  }), []);
  const [url, setUrl] = useState("");
  const [branche, setBranche] = useState("");
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const analyze = trpc.geo.analyze.useMutation({
    onSuccess: (res) => {
      if (res.success && res.data) {
        setAnalysisData(res.data);
        setAnalysisError(null);
      } else {
        setAnalysisError((res as { error?: string }).error || "Unbekannter Fehler");
      }
    },
    onError: (err) => {
      setAnalysisError(err.message);
    },
  });

  const handleAnalyze = () => {
    if (!url.trim() || !isAdminUser) return;
    setAnalysisError(null);
    analyze.mutate({ url: url.trim() });
  };

  const handleReset = () => {
    setAnalysisData(null);
    setAnalysisError(null);
    analyze.reset();
    setUrl("");
  };

  if (analysisData) return <GeoResultsView data={analysisData} onReset={handleReset} />;
  if (analyze.isPending) return <LoadingScreen url={url} />;

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav current="/lokal" accentColor={C.teal} />
      {/* CI-Akzentlinie */}
      <div style={{ height: "4px", backgroundColor: C.teal }} />
      {/* Hero */}
      <section className="w-full bg-black overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-stretch min-h-[520px]">
          {/* Mobile Titel */}
          <div className="block md:hidden px-8 pt-10 pb-6 z-10">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>Marketing Barometer Suite</p>
            <h1 className="font-black uppercase leading-none text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(3rem, 12vw, 5rem)" }}>Lokal<br />Barometer™</h1>
            <p className="text-gray-300 text-sm mt-4 max-w-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>Ich prüfe, wie gut dein Unternehmen lokal gefunden wird – von Google Maps bis Schema.org.</p>
          </div>
          {/* Bild */}
          <div className="w-full md:w-[42%] md:order-2 relative overflow-hidden" style={{ minHeight: "320px" }}>
            <img src={PORTRAIT_IMG} alt="Larissa Girenko – Lokal-Marketing-Expertin" className="w-full h-full object-cover object-top" style={{ minHeight: "320px" }} />
          </div>
          {/* Desktop Text */}
          <div className="hidden md:flex flex-1 flex-col justify-center px-12 py-16 md:order-1">
            <p className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>Marketing Barometer Suite</p>
            <h1 className="font-black uppercase leading-none text-white mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(3rem, 6vw, 5.5rem)" }}>Lokal<br />Barometer™</h1>
            <p className="text-gray-300 text-sm mb-8 max-w-md" style={{ fontFamily: "'DM Sans', sans-serif" }}>Ich prüfe, wie gut dein Unternehmen lokal gefunden wird – von Google Maps bis Schema.org – und liefere dir konkrete Optimierungsschritte.</p>
            <div className="flex flex-wrap gap-5">
              <div className="border-l-2 pl-4" style={{ borderColor: C.teal }}>
                <p className="text-white font-bold text-2xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>8</p>
                <p className="text-gray-400 text-xs tracking-widest uppercase">Dimensionen</p>
              </div>
              <div className="border-l-2 pl-4" style={{ borderColor: C.teal }}>
                <p className="text-white font-bold text-2xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>135+</p>
                <p className="text-gray-400 text-xs tracking-widest uppercase">Kriterien</p>
              </div>
              <div className="border-l-2 pl-4" style={{ borderColor: C.teal }}>
                <p className="text-white font-bold text-2xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>PDF</p>
                <p className="text-gray-400 text-xs tracking-widest uppercase">Report inklusive</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Badge */}
      <div className="flex justify-center py-6 border-b border-gray-200">
        <div className="flex items-center gap-2 border border-gray-300 px-5 py-2">
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: C.teal }} />
          <span className="text-xs font-bold uppercase tracking-widest text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>Kostenlose Vorschau · Vollversion ab 97 €</span>
        </div>
      </div>
      {/* Formular */}
      <section className="max-w-3xl mx-auto w-full px-6 py-16">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>Kostenlose Vorschau</p>
          <h2 className="font-black uppercase leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 6vw, 3.5rem)" }}>Lokal<br />Barometer™</h2>
          <p className="text-gray-500 text-sm max-w-lg mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>Gib deine Website-URL ein. Ich prüfe 8 Dimensionen lokaler Sichtbarkeit – von Google Maps bis Schema.org – und liefere dir konkrete Optimierungsschritte.</p>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>Website-URL *</label>
            <div className="flex gap-0 border-2 bg-white transition-colors" style={{ borderColor: "#e5e7eb" }}>
              <input
                type="text" value={url} onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAnalyze()}
                placeholder="www.dein-unternehmen.de"
                className="flex-1 bg-transparent text-black placeholder-gray-300 px-4 py-3.5 text-sm outline-none"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              />
              <button onClick={handleAnalyze} disabled={!url.trim()}
                className="disabled:opacity-30 text-white font-bold text-xs tracking-widest uppercase px-6 py-3.5 transition-colors whitespace-nowrap"
                style={{ backgroundColor: C.teal, fontFamily: "'DM Sans', sans-serif" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = C.tealDark)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = C.teal)}
              >Vorschau starten →</button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>Branche (optional)</label>
            <select value={branche} onChange={(e) => setBranche(e.target.value)}
              className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none bg-white"
              style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {BRANCHEN.map((b) => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            <span className="text-gray-400 text-xs uppercase tracking-widest mr-2">Beispiele:</span>
            {["jollydog.de", "metzgerei-mueller.de", "zahnarzt-berlin.de"].map((example) => (
              <button
                key={example}
                onClick={() => setUrl(example)}
                className="text-xs font-medium border px-3 py-1 transition-colors bg-white"
                style={{ color: C.muted, borderColor: "#e5e7eb" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = C.teal; (e.currentTarget as HTMLButtonElement).style.borderColor = C.teal; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = C.muted; (e.currentTarget as HTMLButtonElement).style.borderColor = "#e5e7eb"; }}
              >
                {example}
              </button>
            ))}
          </div>
          {analyze.isError && (
            <div className="border-l-4 border-red-500 bg-red-50 px-4 py-3">
              <p className="text-sm text-red-700" style={{ fontFamily: "'DM Sans', sans-serif" }}>{analyze.error?.message || "Analyse fehlgeschlagen. Bitte erneut versuchen."}</p>
            </div>
          )}
        </div>
      </section>
      {/* Dimensionen-Kacheln */}
      <section className="border-t border-gray-200 py-16 px-6" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[
              { icon: "/manus-storage/icon_lokale_identitaet_02a64c71.png", label: "Lokale Identität", desc: "Adresse, Stadtname, Region" },
              { icon: "/manus-storage/23_1ab7147e.svg", label: "Lokale SEO-Signale", desc: "Schema.org, Meta-Tags, NAP" },
              { icon: "/manus-storage/icon_google_business_a5d1d0a7.png", label: "Google Business", desc: "Maps, GMB, Bewertungen" },
              { icon: "/manus-storage/icon_vertrauen_5befd394.png", label: "Vertrauen", desc: "Impressum, Datenschutz, Zertifikate" },
              { icon: "/manus-storage/icon_lokale_inhalte_2668a69f.png", label: "Lokale Inhalte", desc: "Regionale Referenzen, Stadtbezüge" },
              { icon: "/manus-storage/icon_mobile_technisch_9312395b.png", label: "Mobile & Technisch", desc: "Mobile-Optimierung, Ladezeit" },
              { icon: "/manus-storage/icon_soziale_signale_c47c0468.png", label: "Soziale Signale", desc: "Social-Media, lokale Community" },
              { icon: "/manus-storage/icon_kontakt_conversion_eeab8a33.png", label: "Kontakt & Conversion", desc: "Telefon, Kontaktformular, CTAs" },
            ].map((f) => (
              <div key={f.label} className="border p-4 text-center bg-white">
                <img src={f.icon} alt="" className="w-7 h-7 mb-2 object-contain" style={{ opacity: 0.85 }} />
                <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>{f.label}</div>
                <div className="text-xs" style={{ color: C.muted, fontFamily: "'DM Sans', sans-serif" }}>{f.desc}</div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>Warum Lokal-SEO entscheidend ist</p>
            <h3 className="font-black uppercase leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}>46% aller Google-Suchen<br />haben lokale Absicht</h3>
            <p className="text-gray-500 text-sm max-w-2xl mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>Wenn dein Unternehmen bei lokalen Suchanfragen nicht erscheint, verlierst du täglich potenzielle Kunden an die Konkurrenz. Der Lokal-Barometer zeigt dir genau, wo du stehst und was du ändern musst.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              { number: "01", title: "URL eingeben", desc: "Gib deine Website-URL ein – keine Anmeldung, kein Abo erforderlich." },
              { number: "02", title: "KI analysiert", desc: "135+ lokale SEO-Kriterien werden in unter 60 Sekunden automatisch geprüft." },
              { number: "03", title: "Bericht erhalten", desc: "Du bekommst einen detaillierten Report mit konkreten Handlungsempfehlungen." },
            ].map((v) => (
              <div key={v.number} className="p-6 border" style={{ borderColor: "#e5e7eb" }}>
                <div className="flex items-start justify-between mb-4">
                  <span className="font-black text-4xl" style={{ color: C.teal, fontFamily: "'Bebas Neue', sans-serif" }}>{v.number}</span>
                </div>
                <h4 className="font-bold text-sm uppercase tracking-widest mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>{v.title}</h4>
                <p className="text-xs text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>{v.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { stat: "46%", label: "aller Google-Suchen", sub: "haben lokale Absicht (Google)" },
              { stat: "76%", label: "lokaler Suchanfragen", sub: "führen zu Ladenbesuchen (Google)" },
              { stat: "28%", label: "lokaler Suchen", sub: "enden in einem Kauf (HubSpot)" },
            ].map((s) => (
              <div key={s.stat} className="p-6 text-center border" style={{ borderColor: "#e5e7eb" }}>
                <div className="font-black text-4xl mb-2" style={{ color: C.teal, fontFamily: "'Bebas Neue', sans-serif" }}>{s.stat}</div>
                <div className="text-sm mb-1" style={{ color: C.text, fontFamily: "'DM Sans', sans-serif" }}>{s.label}</div>
                <div className="text-xs" style={{ color: C.muted, fontFamily: "'DM Sans', sans-serif" }}>{s.sub}</div>
              </div>
            ))}
          </div>
          <div className="mt-12 border-l-4 pl-6 py-2 max-w-2xl mx-auto" style={{ borderColor: C.teal }}>
            <p className="text-sm italic text-gray-600 mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>"Lokale Sichtbarkeit ist kein Zufall – sie ist das Ergebnis von konsequenter Optimierung. Der Lokal-Barometer macht das messbar."</p>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>– Larissa Girenko</p>
          </div>
          <div className="mt-12 p-8 border border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: "/manus-storage/icon_google_business_a5d1d0a7.png", label: "Google Maps", desc: "Einbindung & Verlinkung geprüft" },
                { icon: "/manus-storage/icon_schema_markup_96dfd08f.png", label: "Schema.org", desc: "LocalBusiness-Markup analysiert" },
                { icon: "/manus-storage/icon_mobile_technisch_9312395b.png", label: "Mobile-First", desc: "Responsive Design bewertet" },
                { icon: "/manus-storage/icon_bewertungen_5c4c89a8.png", label: "Bewertungen", desc: "Review-Signale ausgewertet" },
              ].map((t) => (
                <div key={t.label} className="text-center p-4">
                  <img src={t.icon} alt="" className="w-9 h-9 mb-3 object-contain" style={{ opacity: 0.85 }} />
                  <div className="font-bold text-sm uppercase tracking-widest mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>{t.label}</div>
                  <div className="text-xs leading-relaxed" style={{ color: C.muted, fontFamily: "'DM Sans', sans-serif" }}>{t.desc}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-12 text-center">
            <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="text-white font-bold text-sm tracking-widest uppercase px-10 py-4 transition-colors"
              style={{ backgroundColor: C.teal, fontFamily: "'DM Sans', sans-serif" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = C.tealDark)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = C.teal)}
            >Jetzt Vorschau starten →</button>
          </div>
        </div>
      </section>
      {/* Larissa-Sektion */}
      <section className="border-t border-gray-200 py-16 px-6 bg-black">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="relative overflow-hidden" style={{ maxHeight: "480px" }}>
                <img src={PORTRAIT_IMG} alt="Larissa Girenko – Lokal-Marketing-Expertin" className="w-full object-cover object-top" style={{ maxHeight: "480px" }} />
              </div>
            </div>
            <div>
              <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>Gegründet & entwickelt von</p>
              <h2 className="font-black uppercase leading-none mb-5 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 5vw, 3rem)" }}>Larissa Girenko</h2>
              <p className="text-gray-300 text-sm leading-relaxed mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Lokale Unternehmen verlieren täglich Kunden an Wettbewerber, die bei Google Maps weiter oben stehen – nicht weil sie besser sind, sondern weil ihre digitale Präsenz optimiert ist. Der Lokal-Barometer zeigt dir genau, wo du aufholen kannst.
              </p>
              <p className="text-gray-400 text-sm leading-relaxed mb-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Basierend auf Google-Forschung, BrightLocal-Studien und dem Stand 2025/2026 – das erste messbare System für lokale Online-Sichtbarkeit, das auch kleine Unternehmen ohne Agentur nutzen können.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[{ value: "8", label: "Dimensionen" }, { value: "135+", label: "Kriterien geprüft" }, { value: "Kostenlos", label: "Vorschau" }, { value: formatPrice(BAROMETER_PRICES.lokal), label: "Vollversion" }].map((stat) => (
                  <div key={stat.label} className="border-l-2 pl-4" style={{ borderColor: C.teal }}>
                    <p className="text-white font-bold text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{stat.value}</p>
                    <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Cross-Selling */}
      <CrossSelling
        headline="Lokal sichtbar – und jetzt?"
        subline="Diese Barometer ergänzen das Lokal-Barometer für maximale Sichtbarkeit in deiner Region."
        items={[
          {
            icon: "/manus-storage/7_8bcad2d2.svg",
            title: "GEO-Barometer™",
            description: "KI-Sichtbarkeit: Werde in ChatGPT, Perplexity und Google AI empfohlen – der nächste Schritt nach lokalem SEO.",
            price: formatPrice(BAROMETER_PRICES.geo),
            href: "/geo-barometer",
            accent: "#115751",
            badge: "Ergänzt Lokal",
          },
          {
            icon: "/manus-storage/23_1ab7147e.svg",
            title: "SEO-Barometer™",
            description: "100+ SEO-Kriterien für mehr organische Sichtbarkeit – lokal und überregional.",
            price: formatPrice(BAROMETER_PRICES.seo),
            href: "/seo-barometer",
            accent: "#0a5c36",
          },
        ]}
      />

      {/* Footer */}
      <footer className="border-t border-gray-200 px-6 py-8" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AgencyLogo withLogo size="sm" />
          </div>
          <div className="flex flex-wrap items-center gap-4 justify-center">
            <a href="/" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Übersicht</a>
            <a href="/sensorik" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sensorik</a>
            <a href="/lokal" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Lokal</a>
            <a href="/geo-barometer" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>GEO</a>
            <a href="/seo-barometer" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>SEO</a>
            <a href="/conversion-barometer" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Conversion</a>
            <a href="/impressum" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Impressum</a>
            <a href="/datenschutz" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Datenschutz</a>
          </div>
          <p className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>© 2026 Werbeagentur Larissa Girenko</p>
        </div>
      </footer>
    </div>
  );
}

function LoadingScreen({ url }: { url: string }) {
  const steps = [
    "Startseite wird geladen …",
    "Lokale Signale werden erkannt …",
    "Google Business Profile wird geprüft …",
    "Branchenverzeichnisse werden analysiert …",
    "Bewertungsplattformen werden geprüft …",
    "Technische Basis wird bewertet …",
    "Schema.org-Markup wird analysiert …",
    "Score wird berechnet …",
  ];
  const [stepIndex, setStepIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setStepIndex((i) => Math.min(i + 1, steps.length - 1)), 7000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6">
      <div style={{ height: "4px", backgroundColor: C.teal, position: "fixed", top: 0, left: 0, right: 0 }}></div>
      <div className="text-center max-w-lg w-full">
        <div className="relative w-40 h-40 mx-auto mb-10">
          <div className="absolute inset-0 border-2 border-gray-200 rounded-full"></div>
          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#115751] animate-spin" style={{ animationDuration: "1.5s" }}></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="font-black text-3xl" style={{ color: C.teal, fontFamily: "'Bebas Neue', sans-serif" }}>LB</div>
              <div className="text-gray-400 text-xs tracking-widest uppercase">Analyse</div>
            </div>
          </div>
        </div>
        <h2 className="font-bold text-2xl uppercase tracking-tight mb-2" style={{ fontFamily: "'Roboto', sans-serif", color: C.text }}>
          Lokal-Analyse läuft
        </h2>
        <p className="text-sm mb-8 truncate max-w-xs mx-auto" style={{ color: C.muted }}>{url}</p>
        <div className="border px-6 py-4 mb-8" style={{ backgroundColor: C.bgGray, borderColor: "#e5e7eb" }}>
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.teal }}></div>
            <span className="text-sm font-medium" style={{ color: "#4b5563" }}>{steps[stepIndex]}</span>
          </div>
        </div>
        <div className="flex justify-center gap-2">
          {steps.map((_, i) => (
            <div key={i} className="w-1.5 h-1.5 rounded-full transition-colors duration-500" style={{ backgroundColor: i <= stepIndex ? C.teal : "#e0e0e0" }} />
          ))}
        </div>
        <p className="text-xs mt-6" style={{ color: C.muted }}>Durchschnittliche Analyse-Zeit: 45–90 Sekunden</p>
      </div>
    </div>
  );
}
